/**
 * TelehealthWaitingRoom Component
 *
 * Pre-call waiting room showing member info and allowing
 * the coordinator to start the call or mark no-show.
 * Includes Voice AI consent workflow when enabled.
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, differenceInMinutes, differenceInSeconds } from 'date-fns';
import {
  Video,
  User,
  Clock,
  Calendar,
  Mail,
  PhoneCall,
  Play,
  XCircle,
  Users,
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Mic,
  MicOff,
} from 'lucide-react';
import { telehealthSessionAPI, telehealthVoiceAIAPI, telehealthConfigAPI } from '@/services/api/telehealth';
import { TelehealthWaitingRoomProps, TelehealthSession, SessionOutcome, VoiceAIConsentStatus } from './types';
import SessionStatusBadge from './SessionStatusBadge';
import VoiceAIConsentModal from './VoiceAIConsentModal';

export default function TelehealthWaitingRoom({
  sessionId,
  onJoinCall,
  onCancel,
  onMarkNoShow,
  className = '',
}: TelehealthWaitingRoomProps) {
  const queryClient = useQueryClient();
  const [countdown, setCountdown] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentPending, setConsentPending] = useState(false);

  // Fetch session details
  const {
    data: session,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['telehealth-session', sessionId],
    queryFn: () => telehealthSessionAPI.getSession(sessionId),
    refetchInterval: 5000, // Poll for updates
  });

  // Fetch Voice AI config
  const { data: voiceAIConfig } = useQuery({
    queryKey: ['telehealth-config'],
    queryFn: () => telehealthConfigAPI.getConfig(),
  });

  // Fetch consent status
  const { data: consentStatus } = useQuery({
    queryKey: ['telehealth-consent', sessionId],
    queryFn: () => telehealthVoiceAIAPI.getConsentStatus(sessionId),
    enabled: !!voiceAIConfig?.voice_ai_enabled,
  });

  // Record consent mutation
  const recordConsentMutation = useMutation({
    mutationFn: (data: { consent_granted: boolean; consent_method: 'verbal' | 'written' | 'digital'; notes?: string }) =>
      telehealthVoiceAIAPI.recordConsent(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-consent', sessionId] });
      setShowConsentModal(false);
      // After consent is recorded, start the session
      startSessionMutation.mutate();
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message;
      setLocalError(`Failed to record consent: ${msg}`);
      setShowConsentModal(false);
    },
  });

  // Join waiting room mutation
  const joinWaitingMutation = useMutation({
    mutationFn: () => telehealthSessionAPI.joinWaitingRoom(sessionId, 'coordinator'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-session', sessionId] });
      setLocalError(null);
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message;
      console.error('Join waiting room error:', msg);
      setLocalError(`Failed to join waiting room: ${msg}`);
    },
  });

  // Start session mutation
  const startSessionMutation = useMutation({
    mutationFn: () => telehealthSessionAPI.startSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-session', sessionId] });
      setLocalError(null);
      onJoinCall();
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message;
      console.error('Start session error:', msg);
      setLocalError(`Failed to start session: ${msg}`);
    },
  });

  // End session (no-show) mutation
  const endSessionMutation = useMutation({
    mutationFn: (outcome: SessionOutcome) =>
      telehealthSessionAPI.endSession(sessionId, {
        outcome,
        outcome_notes: 'Member did not join the waiting room',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-session', sessionId] });
      setLocalError(null);
      onMarkNoShow?.();
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail) || err.message;
      console.error('End session error:', msg);
      setLocalError(`Failed to end session: ${msg}`);
    },
  });

  // Auto-join waiting room when component mounts
  useEffect(() => {
    if (session && session.status === 'scheduled' && !session.coordinator_joined_at) {
      joinWaitingMutation.mutate();
    }
  }, [session]);

  // Countdown timer
  useEffect(() => {
    if (!session) return;

    const scheduledTime = new Date(session.scheduled_start_time);
    const updateCountdown = () => {
      const now = new Date();
      const diffMinutes = differenceInMinutes(scheduledTime, now);
      const diffSeconds = differenceInSeconds(scheduledTime, now) % 60;

      if (diffMinutes < 0) {
        setCountdown('Ready to start');
      } else if (diffMinutes === 0) {
        setCountdown(`${diffSeconds}s`);
      } else if (diffMinutes < 60) {
        setCountdown(`${diffMinutes}m ${diffSeconds}s`);
      } else {
        setCountdown(format(scheduledTime, 'h:mm a'));
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [session]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${className}`}>
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (queryError || !session) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-[400px] ${className}`}>
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Failed to load session</p>
        <button
          onClick={() => refetch()}
          className="mt-4 text-teal-600 hover:underline flex items-center gap-1"
        >
          <RefreshCw className="h-4 w-4" />
          Try again
        </button>
      </div>
    );
  }

  const scheduledTime = new Date(session.scheduled_start_time);
  const memberJoined = !!session.member_joined_waiting_at;
  const coordinatorJoined = !!session.coordinator_joined_at;
  const canStart = session.status === 'waiting' || session.status === 'scheduled';
  const isActive = session.status === 'in_progress';

  return (
    <div className={`max-w-2xl mx-auto ${className}`}>
      {/* Error message */}
      {localError && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 dark:text-red-300 font-medium">Error</p>
            <p className="text-red-600 dark:text-red-400 text-sm">{localError}</p>
          </div>
          <button
            onClick={() => setLocalError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <p className="mb-2 text-[13px] text-ink-500">
          {session.is_audio_only ? 'Audio call' : 'Video call'}
        </p>
        <SessionStatusBadge status={session.status} size="lg" />
      </div>

      {/* Session Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-6">
        {/* Member Info */}
        <div className="p-6 border-b dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-xl font-semibold">
              {session.member_name?.charAt(0) || 'M'}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {session.member_name || `Member #${session.member_id}`}
              </h2>
              {session.event_title && (
                <p className="text-gray-600 dark:text-gray-400">{session.event_title}</p>
              )}
            </div>
          </div>

          {/* Contact info */}
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
            {session.member_phone && (
              <a
                href={`tel:${session.member_phone}`}
                className="flex items-center gap-1.5 hover:text-teal-600"
              >
                <PhoneCall className="h-4 w-4" />
                {session.member_phone}
              </a>
            )}
            {session.member_email && (
              <a
                href={`mailto:${session.member_email}`}
                className="flex items-center gap-1.5 hover:text-teal-600"
              >
                <Mail className="h-4 w-4" />
                {session.member_email}
              </a>
            )}
          </div>
        </div>

        {/* Schedule Info */}
        <div className="p-6 bg-gray-50 dark:bg-gray-850">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Scheduled Date</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {format(scheduledTime, 'EEEE, MMM d, yyyy')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Scheduled Time</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {format(scheduledTime, 'h:mm a')} ({session.scheduled_duration_minutes} min)
                </p>
              </div>
            </div>
          </div>

          {/* Countdown */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {differenceInMinutes(scheduledTime, new Date()) < 0
                ? 'Session should have started'
                : 'Starting in'}
            </p>
            <p className="text-3xl font-bold text-teal-600">{countdown}</p>
          </div>
        </div>

        {/* Participant Status */}
        <div className="p-6 border-t dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Participant Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Member</span>
              </div>
              {memberJoined ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  In waiting room
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  Not yet joined
                </span>
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">You (Coordinator)</span>
              </div>
              {coordinatorJoined ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  Ready
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-yellow-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Joining...
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Voice AI Status Indicator */}
      {voiceAIConfig?.voice_ai_enabled && (
        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <div className="flex items-center gap-3">
            <Mic className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <div className="flex-1">
              <p className="font-medium text-purple-800 dark:text-purple-300">Voice AI Transcription Available</p>
              <p className="text-sm text-purple-600 dark:text-purple-400">
                {consentStatus?.consent_status === 'granted'
                  ? 'Member has granted consent for AI transcription'
                  : consentStatus?.consent_status === 'denied'
                  ? 'Member declined AI transcription'
                  : 'You will be asked to obtain consent before starting'}
              </p>
            </div>
            {consentStatus?.consent_status === 'granted' && (
              <CheckCircle className="h-5 w-5 text-green-600" />
            )}
            {consentStatus?.consent_status === 'denied' && (
              <MicOff className="h-5 w-5 text-gray-500" />
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={onCancel}
          className="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          Cancel
        </button>

        {canStart && (
          <>
            <button
              onClick={() => endSessionMutation.mutate('no_show_member')}
              disabled={endSessionMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <XCircle className="h-5 w-5" />
              Mark No-Show
            </button>

            <button
              onClick={() => {
                // Check if Voice AI is enabled and consent hasn't been obtained yet
                if (
                  voiceAIConfig?.voice_ai_enabled &&
                  voiceAIConfig?.voice_ai_auto_prompt_consent &&
                  (!consentStatus?.consent_status || consentStatus.consent_status === 'not_requested')
                ) {
                  setShowConsentModal(true);
                } else {
                  startSessionMutation.mutate();
                }
              }}
              disabled={startSessionMutation.isPending || recordConsentMutation.isPending}
              className="flex items-center gap-2 px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-lg font-medium shadow-lg"
            >
              {startSessionMutation.isPending || recordConsentMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Play className="h-5 w-5" />
              )}
              Start Call
            </button>
          </>
        )}

        {isActive && (
          <button
            onClick={onJoinCall}
            className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-lg font-medium shadow-lg"
          >
            <Video className="h-5 w-5" />
            Rejoin Call
          </button>
        )}
      </div>

      {/* Voice AI Consent Modal */}
      {showConsentModal && session && (
        <VoiceAIConsentModal
          open={showConsentModal}
          session={session}
          consentScript={voiceAIConfig?.consent_script_text}
          onConsent={(granted, method, notes) => {
            recordConsentMutation.mutate({
              consent_granted: granted,
              consent_method: method,
              notes,
            });
          }}
          onClose={() => setShowConsentModal(false)}
        />
      )}

      {/* Tips */}
      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Tips</h4>
        <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
          <li>- Ensure your camera and microphone are working</li>
          <li>- Use a quiet, well-lit space</li>
          <li>- Have the member's information ready</li>
          <li>- You can start the call even if the member hasn't joined yet</li>
        </ul>
      </div>
    </div>
  );
}
